/* =============================================================================
   VIEWS / ADMIN / SUPPLIERS-ADMIN — Lieferanten-Stammdaten (Phase E4a, S163)
   =============================================================================
   Subtab „Lieferanten" der Gruppe „Ware". CRUD über Manager-gated SECURITY-DEFINER-
   RPCs (Migration phase_121); die UI zeigt die Buttons, der Server gated hart
   (Fehlertoast bei fehlender Rolle). Hinter Flag `ingredientsDb`.

   Die Zutat↔Lieferant-Zuordnung (Preis/Einkaufseinheit/Hauptlieferant) lebt im
   Zutaten-Editor (ingredients-admin.js); hier nur die Lieferanten-Stammdaten.
   Non-destruktive Renders; Modal an Draft gebunden (kein Re-Render bei Tastendruck);
   Inline-Handler ohne window.func() (CSP/dom-bindings).
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';

let _cache = [];     // supplier_overview()-Zeilen (camelCase)
let _draft = null;   // aktuelle Bearbeitung

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }

// -----------------------------------------------------------------------------
// Tab-Render
// -----------------------------------------------------------------------------
export async function renderSuppliersAdmin() {
  const el = document.getElementById('tab-suppliers');
  if (!el) return;
  if (!flagOn()) {
    el.innerHTML = `<div class="card"><p class="hint">Lieferanten sind deaktiviert (Feature-Flag <code>ingredientsDb</code>).</p></div>`;
    return;
  }
  el.innerHTML = `<div class="card"><p class="hint">Lade Lieferanten…</p></div>`;
  try {
    _cache = await storage().suppliers.overview();
  } catch (e) {
    console.warn('[suppliers] overview:', e);
    _cache = [];
  }
  el.innerHTML = renderListHtml(_cache);
}

function renderListHtml(items) {
  const rows = items.map(renderRowHtml).join('');
  return `
    <div class="card">
      <div style="display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;">
        <div>
          <h2 style="margin:0;">Lieferanten</h2>
          <p class="hint" style="margin:4px 0 0;">Bezugsquellen für Zutaten. Preis &amp; Einkaufseinheit je Zutat pflegst du im Zutaten-Editor (Abschnitt „Lieferanten"). ${items.length} Lieferant(en).</p>
        </div>
        <button class="btn btn-primary btn-sm" onclick="openSupplierForm()">+ Lieferant anlegen</button>
      </div>
      <div style="margin-top:14px; display:flex; flex-direction:column; gap:6px;">
        ${rows || '<div class="hint" style="padding:16px; text-align:center;">Noch keine Lieferanten. Lege den ersten an.</div>'}
      </div>
    </div>`;
}

function renderRowHtml(s) {
  const meta = [s.contactName, s.email, s.phone].filter(Boolean).map(htmlEscape).join(' · ');
  const inactive = s.active ? '' : ' <span class="hint" style="color:var(--danger);">(inaktiv)</span>';
  const cnt = Number(s.ingredientCount) > 0
    ? `<span class="hint" style="font-size:.82rem; white-space:nowrap;">${s.ingredientCount} Zutat(en)</span>` : '';
  return `
    <div data-supplier="${htmlEscape(s.id)}"
         style="display:grid; grid-template-columns:1fr auto auto auto; gap:12px; align-items:center; padding:10px 14px; background:var(--bg-soft); border:1px solid var(--border); border-radius:8px;">
      <div style="min-width:0;">
        <div style="font-weight:600;">${htmlEscape(s.name)}${inactive}</div>
        <div class="hint" style="font-size:.8rem;">${meta || '—'}${s.customerNumber ? ' · Kd-Nr. ' + htmlEscape(s.customerNumber) : ''}</div>
      </div>
      ${cnt}
      <button class="btn btn-ghost btn-sm" onclick="openSupplierForm('${htmlEscape(s.id)}')">Bearbeiten</button>
      <button class="btn btn-ghost btn-sm" style="color:var(--danger);" onclick="deleteSupplier('${htmlEscape(s.id)}')" title="Löschen">✕</button>
    </div>`;
}

// -----------------------------------------------------------------------------
// Formular (Modal, Draft-gebunden → non-destruktiv)
// -----------------------------------------------------------------------------
export function openSupplierForm(id) {
  const existing = id ? _cache.find(x => x.id === id) : null;
  _draft = existing
    ? JSON.parse(JSON.stringify(existing))
    : { name: '', contactName: '', email: '', phone: '', address: '', customerNumber: '', note: '', active: true };
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (!content || !overlay) return;
  content.innerHTML = renderFormHtml(_draft, !!existing);
  overlay.classList.add('active');
}

function renderFormHtml(d, isEdit) {
  return `
    <div class="modal-header">
      <h2>${isEdit ? 'Lieferant bearbeiten' : 'Neuer Lieferant'}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div class="field"><label>Name *</label>
      <input id="sup-name" type="text" value="${htmlEscape(d.name || '')}" oninput="supplierDraftSet('name', this.value)"></div>
    <div class="field-row">
      <div class="field"><label>Ansprechpartner</label>
        <input type="text" value="${htmlEscape(d.contactName || '')}" oninput="supplierDraftSet('contactName', this.value)"></div>
      <div class="field"><label>Kundennummer</label>
        <input type="text" value="${htmlEscape(d.customerNumber || '')}" oninput="supplierDraftSet('customerNumber', this.value)"></div>
    </div>
    <div class="field-row">
      <div class="field"><label>E-Mail</label>
        <input type="email" value="${htmlEscape(d.email || '')}" oninput="supplierDraftSet('email', this.value)"></div>
      <div class="field"><label>Telefon</label>
        <input type="text" value="${htmlEscape(d.phone || '')}" oninput="supplierDraftSet('phone', this.value)"></div>
    </div>
    <div class="field"><label>Adresse</label>
      <input type="text" value="${htmlEscape(d.address || '')}" oninput="supplierDraftSet('address', this.value)"></div>
    <div class="field"><label>Notiz</label>
      <input type="text" value="${htmlEscape(d.note || '')}" oninput="supplierDraftSet('note', this.value)" placeholder="z. B. Liefertage, Mindestbestellwert"></div>
    <label class="med-tag-chip ${d.active ? 'active' : ''}" style="display:inline-flex; margin-top:8px;">
      <input type="checkbox" ${d.active ? 'checked' : ''} onchange="supplierDraftSet('active', this.checked)"> aktiv
    </label>
    <div class="btn-row" style="margin-top:16px;">
      <button class="btn btn-primary" onclick="saveSupplier()">Speichern</button>
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>`;
}

export function supplierDraftSet(field, value) { if (_draft) _draft[field] = value; }

export async function saveSupplier() {
  if (!_draft) return;
  if (!_draft.name || !_draft.name.trim()) { showToast('Name ist Pflicht', 'error'); return; }
  try {
    await storage().suppliers.upsert(_draft);
    showToast('Lieferant gespeichert', 'success');
    if (typeof window.closeModal === 'function') window.closeModal();
    await renderSuppliersAdmin();
  } catch (e) {
    console.error('[suppliers] save:', e);
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function deleteSupplier(id) {
  const s = _cache.find(x => x.id === id);
  if (!(await window.styledConfirm({ title: `Lieferant „${s ? s.name : id}" löschen?`, body: 'Zutat-Verknüpfungen werden mitgelöst; gesyncte Einkaufspreise bleiben als manueller Stand erhalten.', danger: true, confirmLabel: 'Löschen' })).confirmed) return;
  try {
    await storage().suppliers.remove(id);
    showToast('Lieferant gelöscht', 'success');
    await renderSuppliersAdmin();
  } catch (e) {
    console.error('[suppliers] delete:', e);
    showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// window-Bindings (CSP: Inline-Handler ohne window.*)
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderSuppliersAdmin, openSupplierForm, supplierDraftSet, saveSupplier, deleteSupplier
  });
}
